import { type Event, type EventParticipant, isRegisteredEventParticipant } from 'models';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { CommentLineIcon, type MenuProps, MinusCircleIcon } from 'ui';
import { useDirectMessageNode } from 'features/chat';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { useGetUserFirstNameWithFallback, useGetUserLastNameWithFallback } from 'features/users/hooks';
import { useDeleteReminderInvitee, useIsViewerEventInvitee } from '../../../../../../hooks';

export const useEventInviteesActions = () => {
  const { t } = useTranslation();
  const isViewerEventInvitee = useIsViewerEventInvitee();
  const { canDeleteUserFromEvent } = usePermissions();
  const { confirm: deleteReminderInvitee, contextHolder, isLoadingDeletingRole } = useDeleteReminderInvitee();
  const { handleDirectMessage } = useDirectMessageNode();
  const getUserFirstNameWithFallback = useGetUserFirstNameWithFallback();
  const getUserLastNameWithFallback = useGetUserLastNameWithFallback();

  const getInviteesActions = useCallback(
    ({ currentInvitee, event }: { currentInvitee: EventParticipant; event: Event }) => {
      const actions: MenuProps['items'] = [];
      const { id } = event;

      if (isViewerEventInvitee(currentInvitee)) {
        return [];
      }

      const email = currentInvitee.email;

      if (isRegisteredEventParticipant(currentInvitee) && email) {
        actions.push({
          icon: <CommentLineIcon />,
          key: 'message',
          label: t('Message'),
          async onClick() {
            await handleDirectMessage({
              _id: currentInvitee.ref,
              email,
              firstName: getUserFirstNameWithFallback(currentInvitee),
              lastName: getUserLastNameWithFallback(currentInvitee),
            });
          },
        });
      }

      if (canDeleteUserFromEvent({ event })) {
        actions.push({
          danger: true,
          icon: <MinusCircleIcon />,
          key: 'remove',
          label: t('Remove from reminder'),
          onClick() {
            deleteReminderInvitee({ eventId: id, invitee: currentInvitee });
          },
        });
      }

      return actions;
    },
    [
      canDeleteUserFromEvent,
      deleteReminderInvitee,
      getUserFirstNameWithFallback,
      getUserLastNameWithFallback,
      handleDirectMessage,
      isViewerEventInvitee,
      t,
    ],
  );

  return {
    contextHolder,
    getInviteesActions,
    isLoadingAction: isLoadingDeletingRole,
  };
};
